import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonLabel", "modal", "modalTitle", "poList"]

  static values = {
    archiveUrl: String,
    frameId: { type: String, default: "po_tracks_table" },
  }

  connect() {
    this.updateButton()
    this.element.addEventListener("print-selection:updated", this.boundOnSelectionUpdated)
    document.addEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.addEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("print-selection:updated", this.boundOnSelectionUpdated)
    document.removeEventListener("turbo:frame-render", this.boundOnFrameRender)
    document.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  get boundOnSelectionUpdated() {
    if (!this._boundOnSelectionUpdated) {
      this._boundOnSelectionUpdated = () => this.updateButton()
    }
    return this._boundOnSelectionUpdated
  }

  get boundOnFrameRender() {
    if (!this._boundOnFrameRender) {
      this._boundOnFrameRender = (event) => {
        if (event.target?.id === this.frameIdValue) {
          setTimeout(() => this.updateButton(), 0)
        }
      }
    }
    return this._boundOnFrameRender
  }

  get boundOnSubmitEnd() {
    if (!this._boundOnSubmitEnd) {
      this._boundOnSubmitEnd = (event) => {
        if (event.detail?.success) {
          setTimeout(() => this.updateButton(), 0)
        }
      }
    }
    return this._boundOnSubmitEnd
  }

  updateButton() {
    if (!this.hasButtonTarget || !this.hasButtonLabelTarget) return

    const { ids, poNums, count } = this.getCompletedToArchive()
    if (count === 0) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("btn-disabled")
      this.buttonLabelTarget.textContent = "Archive Completed"
    } else {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("btn-disabled")
      this.buttonLabelTarget.textContent = `Archive Completed (${count})`
    }
    this._pendingIds = ids
    this._pendingPoNums = poNums
  }

  getCompletedToArchive() {
    const rows = document.querySelectorAll('[data-print-selection-target="row"]')
    const checkboxes = document.querySelectorAll('[data-print-selection-target="rowCheckbox"]')
    const checkedIds = new Set(
      Array.from(checkboxes)
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
    )

    const completed = []
    rows.forEach((row) => {
      const status = row.dataset.status
      const id = row.querySelector('[data-print-selection-target="rowCheckbox"]')?.value
      const poNum = row.dataset.poNum
      if (status === "Completed" && id) {
        const isRelevant = checkedIds.size === 0 ? true : checkedIds.has(id)
        if (isRelevant) {
          completed.push({ id, poNum })
        }
      }
    })

    return {
      ids: completed.map((p) => p.id),
      poNums: completed.map((p) => p.poNum || p.id),
      count: completed.length,
    }
  }

  openModal() {
    const { ids, poNums, count } = this.getCompletedToArchive()
    if (count === 0) return

    this._pendingIds = ids
    this._pendingPoNums = poNums

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = `Archive Completed Purchase Orders (${count})`
    }
    if (this.hasPoListTarget) {
      this.poListTarget.innerHTML = ""
      poNums.forEach((n) => {
        const badge = document.createElement("span")
        badge.className = "badge badge-outline"
        badge.textContent = n
        this.poListTarget.appendChild(badge)
      })
    }
    this.modalTarget.showModal()
  }

  closeModal() {
    this.modalTarget.close()
  }

  archive() {
    const ids = this._pendingIds
    if (!ids || ids.length === 0) return

    fetch(this.archiveUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
        Accept: "application/json",
      },
      body: JSON.stringify({ ids }),
    })
      .then((response) => {
        if (response.ok) {
          this.closeModal()
          window.toast?.("Archived successfully.", "success")
          window.Turbo?.visit(window.location.href, { action: "replace" })
        } else {
          window.toast?.("Failed to archive purchase orders.", "error")
        }
      })
      .catch(() => {
        window.toast?.("Failed to archive purchase orders.", "error")
      })
  }
}
